import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Loader } from "@src/components/atoms";
import AppUtils from "@src/utils/appUtils";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
} from "@tanstack/react-table";

import { formatMass } from "@signolteam/shared/units-of-measure";
import { useWindowDimensions } from "@src/utils/mediaQuery";
import { COMMON_CONTROLS } from "@src/stories/utils/common-controls";

const getColumns = (events, industry, width) => {
  const isShipping = industry === "shipping";
  const isMobile = width < 768; //under 768px
  const isMobileLG = width < 1024; //under 1024px

  const noRouteData = events.every(
    (item) => item.origin === "UNKNOWN" && item.destination === "UNKNOWN",
  );
  const noSavingRange = events.every((item) =>
    item?.behaviours?.every((b) => b.succeeded === false && b.CO2SavedKg === 0),
  );

  const noBehavioursRange = events.every(
    (item) =>
      item?.behaviours.reduce(
        (count, behaviour) =>
          behaviour.succeeded || behaviour.succeededSelfReported
            ? ++count
            : count,
        0,
      ) ===
      events?.[0]?.behaviours.reduce(
        (count, behaviour) =>
          behaviour.succeeded || behaviour.succeededSelfReported
            ? ++count
            : count,
        0,
      ),
  );
  const teamBvrNumber = events?.[0]?.behaviours?.length;

  let aviationColumns = [
    {
      header: (
        <div className="text-left font-bold hover:text-primary-500 cursor-pointer pl-2">
          Departure
        </div>
      ),
      id: "departure",
      accessorFn: (flight) =>
        AppUtils.datetimeTinyFormatterUTC(flight.ocurrenceDatetime),
      cell: (props) => <div className="pl-2">{props.getValue()}</div>,
      sortingFn: (a, b) => AppUtils.sortDatetimes(a, b),
      enableSorting: true,
    },
    {
      header: (
        <div
          className={`${!noRouteData ? "font-bold hover:text-primary-500 cursor-pointer" : "font-bold"} text-left `}
        >
          {isShipping ? "Voyage" : "Sector"}
        </div>
      ),
      id: "route",
      accessorFn: (flight) =>
        AppUtils.returnJourneyString(flight.origin, flight.destination),
      cell: (props) => <div>{props.getValue()}</div>,
      enableSorting: !noRouteData,
    },
    {
      header: (
        <div className="text-left font-bold hover:text-primary-500">
          <span className="hidden sm:inline-block pr-1">
            CO<sub>2</sub>
          </span>
          <span className="sm:hidden">
            CO<sub>2</sub>{" "}
          </span>
          saved
        </div>
      ),
      id: "CO2",
      accessorFn: (flight) => {
        let saved = 0;
        flight?.behaviours.forEach((b) => {
          if (b.succeeded && b.CO2SavedKg > 0) {
            saved += b.CO2SavedKg;
          }
        });
        return saved;
      },
      cell: (props) => (
        <div className="px-1 text-primary-500 font-bold">
          {formatMass(industry, Number(props.getValue())).formattedValue}
        </div>
      ),
      enableSorting: !noSavingRange,
    },
    {
      header: (
        <div className="font-bold hover:text-primary-500 leading-tight">
          Behaviours
          <br />
          Performed
        </div>
      ),
      id: "behaviours",
      accessorFn: (event) => {
        const succeeded = event?.behaviours.reduce(
          (count, behaviour) =>
            behaviour.succeeded || behaviour.succeededSelfReported
              ? ++count
              : count,
          0,
        );
        const total = teamBvrNumber || event?.behaviours.length;
        let qualified = true;

        return { succeeded, total, qualified };
      },
      cell: (props) => (
        <div className="flex justify-center my-4">
          <div>
            <span className="text-lg">{props.getValue().succeeded}</span> /{" "}
            {props.getValue().total}
          </div>
          {!props.getValue().qualified && (
            <span className="h-2 w-2 rounded-full bg-red-400"></span>
          )}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a =
          rowA.original?.behaviours?.filter(
            (b) => b.succeeded || b.succeededSelfReported,
          ).length || 0;

        const b =
          rowB.original?.behaviours?.filter(
            (b) => b.succeeded || b.succeededSelfReported,
          ).length || 0;

        return AppUtils.sortNumbers(a, b);
      },
      enableSorting: !noBehavioursRange,
    },
  ];

  let shippingColumns = [
    {
      header: (
        <div className="font-bold hover:text-primary-500 cursor-pointer">
          {"Reporting interval"}
        </div>
      ),
      id: "departure",
      accessorFn: (event) => {
        const start = AppUtils.datetimeTinyFormatterUTC(
          event.ocurrenceDatetime,
        );
        const end = AppUtils.datetimeTinyFormatterUTC(event.arrivalDatetime);
        return {
          start,
          end,
          reportType: event.reportType,
          origin: event.origin,
          destination: event.destination,
        };
      },
      cell: (props) => (
        <div
          className={`flex flex-col lg:flex-row justify-start  ${!!isMobileLG || !!isMobile ? "gap-1" : "gap-16"} ${
            !!isMobileLG || !!isMobile ? "my-4" : "ml-24 my-4"
          }`}
        >
          <div
            className={` flex ${
              !!isMobileLG || !!isMobile
                ? "justify-center"
                : "justify-start min-w-64"
            }  gap-2 leading-normal`}
          >
            <span className={`text-center`}>{props.getValue().start}</span>
            <span>{"  >  "}</span>
            <span className={`text-center`}>{props.getValue().end}</span>
          </div>
          <div className="flex justify-center lg:justify-start gap-2 leading-normal">
            {AppUtils.returnJourneyString(
              props.getValue().origin,
              props.getValue().destination,
            )}
          </div>
        </div>
      ),
      sortingFn: (a, b) => AppUtils.sortDatetimes(a, b),
      width: "auto",
    },
    {
      header: (
        <div className="font-bold hover:text-primary-500">
          <span className="hidden sm:inline-block pr-1">
            CO<sub>2</sub>
          </span>
          <span className="sm:hidden">
            CO<sub>2</sub>{" "}
          </span>
          saved
        </div>
      ),
      id: "CO2",
      accessorFn: (flight) => {
        let saved = 0;
        flight.behaviours.forEach((b) => {
          if (b.succeeded && b.CO2SavedKg > 0) {
            saved += b.CO2SavedKg;
          }
        });
        return saved;
      },
      cell: (props) => (
        <div className="px-1 text-primary-500 font-bold text-center">
          {formatMass(industry, Number(props.getValue())).formattedValue}
        </div>
      ),
      enableSorting: !noSavingRange,
      width: !!isMobileLG ? 200 : 300,
    },
    {
      header: (
        <div className="font-bold hover:text-primary-500 leading-tight">
          Behaviours
          <br />
          Performed
        </div>
      ),
      id: "behaviours",
      show: !isMobile,
      accessorFn: (event) => {
        const succeeded = event.behaviours.reduce(
          (count, behaviour) =>
            behaviour.succeeded || behaviour.succeededSelfReported
              ? ++count
              : count,
          0,
        );
        const total = teamBvrNumber || event.behaviours.length;
        let qualified = true;

        return { succeeded, total, qualified };
      },
      cell: (props) => (
        <div className="flex justify-center">
          <div>
            <span className="text-lg">{props.getValue().succeeded}</span> /{" "}
            {props.getValue().total}
          </div>
          {!props.getValue().qualified && (
            <span className="h-2 w-2 rounded-full bg-red-400"></span>
          )}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a =
          rowA.original?.behaviours?.filter(
            (b) => b.succeeded || b.succeededSelfReported,
          ).length || 0;

        const b =
          rowB.original?.behaviours?.filter(
            (b) => b.succeeded || b.succeededSelfReported,
          ).length || 0;

        return AppUtils.sortNumbers(a, b);
      },
      enableSorting: !noBehavioursRange,
      width: !!isMobile ? 90 : 200,
    },
  ];
  return !!isShipping ? shippingColumns : aviationColumns;
};

const EventsTable = (props) => {
  const {
    events = [],
    isLoading,
    onEventClick,
    className,
    industry = "shipping",
  } = props;

  const { width } = useWindowDimensions();
  const [data, _setData] = useState(events);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [sorting, setSorting] = useState([]);

  const table = useReactTable({
    data,
    columns: getColumns(events, industry, width),
    state: {
      columnVisibility,
      sorting,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });

  useEffect(
    (prev) => {
      setColumnVisibility({
        ...prev,
        CO2: width > 768,
      });
    },
    [width],
  );

  useEffect(() => {
    _setData(events);
  }, [events]);

  return (
    <div className={className}>
      {isLoading && <Loader size="2xl" />}

      {!isLoading && !!table && data.length > 0 && (
        <div className="p-2">
          <table className="w-full divide-y divide-gray-200 ">
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b border-gray-500">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      onClick={header.column.getToggleSortingHandler()}
                      className={
                        header.column.getCanSort()
                          ? "cursor-pointer select-none"
                          : ""
                      }
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`border-b border-gray-200 ${onEventClick ? "cursor-pointer hover:bg-primary-100 hover:font-bold" : ""}`}
                  onClick={() => {
                    !!onEventClick && onEventClick(row.original.id);
                  }}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
            <tfoot>
              {table.getFooterGroups().map((footerGroup) => (
                <tr key={footerGroup.id}>
                  {footerGroup.headers.map((header) => (
                    <th key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.footer,
                            header.getContext(),
                          )}
                    </th>
                  ))}
                </tr>
              ))}
            </tfoot>
          </table>
          <div className="h-4" />
        </div>
      )}
    </div>
  );
};

export const eventsTableControls = {
  events: {
    propType: PropTypes.array,
    description: "Array of events",
    controlType: "object",
    defaultValue: [],
  },
  isLoading: COMMON_CONTROLS.isLoading,
  onEventClick: {
    propType: PropTypes.func,
    description: "Event click handler",
    disable: true,
  },
  className: COMMON_CONTROLS.className,
  industry: COMMON_CONTROLS.industry,
};

EventsTable.propTypes = AppUtils.getPropTypeFromControls(eventsTableControls);

export default EventsTable;
